import React, { useEffect, useState, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ToggleButton from '@mui/material/ToggleButton';
import Alert from '@mui/material/Alert';

import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import useGlobalStyles from '../styles';
import Header from '../components/Header';
import { useAppDispatch, useAppSelector } from '../state/store';
import {
  getCurrentLocation,
  getCurrentLocationChildLocations,
  getLocSensorStatusData,
  getSensorsById,
  getSelectedVars,
} from '../state/selectors';
import { setSensorsById, setSensorVars } from '../state/actions';
import { VarName } from '../utils/varNames';
import { knownOfflineTag } from '../Widgets/OfflineView/helpers';
import { BasicSensorType, DataTreeItem, GatewaySensorType, SensorLatest } from '../services/api';
import OfflineLocSummary from '../Widgets/OfflineView/OfflineLocSummary';
import OfflineGatewaySummary from '../Widgets/OfflineView/OfflineGatewaySummary';
import OfflineSensorSummary from '../Widgets/OfflineView/OfflineSensorSummary';
import {
  fetchAllSensorsInLocation,
  fetchSensorLatest,
  updateSensorProperties,
} from '../services/apiService';
import Loading from '../components/Loading';

export enum ListOptions {
  all = 'all',
  untagged = 'untagged',
}

// Helper Component: Renders the list of sub-locations when a parent location is only partially offline.
function PartialOfflineList({
  offlineSensors,
  locSensors,
  excludeLocations, // fully offline locations
  listType,
  handleTagging,
}: {
  offlineSensors: DataTreeItem[];
  locSensors: DataTreeItem[];
  excludeLocations: Set<string>;
  listType: ListOptions;
  handleTagging: (sensors: DataTreeItem[], isTagged: boolean) => void;
}) {
  const sensorsById = useAppSelector(getSensorsById);

  // Identify unique sub-locations that have offline sensors, excluding fully offline parents
  const validSubLocations = useMemo(() => {
    const locations = offlineSensors
      .map((item) => item.location)
      .filter((loc): loc is string => !!loc && !excludeLocations.has(loc));
    return Array.from(new Set(locations));
  }, [offlineSensors, excludeLocations]);

  if (validSubLocations.length === 0) return null;

  return (
    <Box>
      {validSubLocations.map((subLocation) => {
        const subLocOfflineSensors = offlineSensors.filter((item) => item.location === subLocation);
        const allSubLocSensors = locSensors.filter((item) => item.location === subLocation);

        if (allSubLocSensors.length === 0) return null;

        // Identify offline gateways
        const offlineGateways = subLocOfflineSensors
          .filter((sensor) => sensorsById.get(sensor.id)?.type === GatewaySensorType.LfWiFiSensor)
          .map((s) => s.id);

        // Filter faulty sensors (exclude those whose gateway is already offline to avoid double reporting)
        const filteredFaultySensors = subLocOfflineSensors.filter((sensor) => {
          const gateway = sensorsById.get(sensor.id)?.gateway;
          return gateway && !offlineGateways.includes(gateway);
        });

        const isLocFullyOffline = allSubLocSensors.length === subLocOfflineSensors.length;

        return (
          <OfflineLocSummary
            key={subLocation}
            locId={subLocation}
            listType={listType}
            locSensors={allSubLocSensors}
            offlineSensors={subLocOfflineSensors}
            handleTagging={handleTagging}
          >
            <Box>
              <OfflineGatewaySummary
                allSensors={allSubLocSensors}
                isLocOffline={isLocFullyOffline}
                listType={listType}
                handleTagging={handleTagging}
                offlineGateways={offlineGateways}
              />
              <OfflineSensorSummary
                faultySensors={filteredFaultySensors}
                listType={listType}
                handleTagging={handleTagging}
              />
            </Box>
          </OfflineLocSummary>
        );
      })}
    </Box>
  );
}

/**
 * Represents a single row for a Child Location.
 * Calculates its own sensor stats to keep the main component clean.
 */
function LocationRow({
  locId,
  allSensors,
  fullyOfflineLocs,
  listType,
  handleTagging,
}: {
  locId: string;
  allSensors: DataTreeItem[];
  fullyOfflineLocs: Set<string>;
  listType: ListOptions;
  handleTagging: (sensors: DataTreeItem[], isTagged: boolean) => void;
}) {
  // Filter sensors specific to this location or its children
  const { locSensors, offlineSensors } = useMemo(() => {
    const relevant = allSensors.filter(
      (item) => item.location === locId || item.location?.startsWith(`${locId}#`)
    );
    const offline = relevant.filter((item) => item?.value !== undefined && item.value <= 0);
    return { locSensors: relevant, offlineSensors: offline };
  }, [allSensors, locId]);

  const isFullyOffline = locSensors.length > 0 && locSensors.length === offlineSensors.length;
  const isPartiallyOffline = offlineSensors.length > 0 && !isFullyOffline;

  return (
    <Box>
      {/* 1. Full Offline Summary */}
      {isFullyOffline && (
        <OfflineLocSummary
          locId={locId}
          listType={listType}
          locSensors={locSensors}
          offlineSensors={offlineSensors}
          handleTagging={handleTagging}
        />
      )}

      {/* 2. Partial Offline Details (Sub-locations) */}
      {isPartiallyOffline && (
        <PartialOfflineList
          offlineSensors={offlineSensors}
          locSensors={locSensors}
          excludeLocations={fullyOfflineLocs}
          listType={listType}
          handleTagging={handleTagging}
        />
      )}
    </Box>
  );
}

// --- Main Component ---
function OfflineView(): JSX.Element {
  const theme = useTheme();
  const globalClasses = useGlobalStyles();
  const dispatch = useAppDispatch();
  const isDesktopView = useMediaQuery(theme.breakpoints.up('sm'));

  // Selectors
  const selectedVars = useAppSelector(getSelectedVars);
  const currentLocation = useAppSelector(getCurrentLocation);
  const currentLocationChildLocations = useAppSelector(getCurrentLocationChildLocations);
  const locSensorStatusData = useAppSelector(getLocSensorStatusData);
  const sensorsById = useAppSelector(getSensorsById);

  const [listType, setListType] = useState(ListOptions.all);
  const [fetchingSensorProps, setFetchingSensorProps] = useState(true);

  // Ensure OnlineStatus variable is selected
  useEffect(() => {
    if (selectedVars.length > 0 && !selectedVars.includes(VarName.OnlineStatus)) {
      dispatch(setSensorVars([...selectedVars, VarName.OnlineStatus]));
    }
  }, [dispatch, selectedVars]);

  // fetch sensory type and its gateway properties for all sensors in location
  useEffect(() => {
    fetchAllSensorsInLocation(currentLocation, ['type', 'gateway', 'tags'])
      .then((sensors) => {
        setFetchingSensorProps(false);
        const sensorProps = sensors.sensors ?? [];
        dispatch(setSensorsById(sensorProps as SensorLatest[]));
      })
      .catch(() => setFetchingSensorProps(false));
  }, [currentLocation, dispatch]);

  // Data Preparation
  const allOnlineData = locSensorStatusData.onlineData.get(VarName.OnlineStatus);

  // filter retired and virtual sensors
  const allSensors = useMemo(() => {
    const filteredRetiredSensors =
      allOnlineData?.filter((item) => item?.value !== undefined && item.value > -2) ?? [];
    const filteredVirtualSensors = filteredRetiredSensors.filter(
      (sensor) => sensorsById.get(sensor.id)?.type !== BasicSensorType.Virtual
    );
    return filteredVirtualSensors;
  }, [allOnlineData, sensorsById]);

  // Determine locations to render (use sublocations as tree if available, else current)
  const sortedChildLocations = useMemo(() => {
    if (currentLocationChildLocations.length === 0) return [currentLocation];
    return [...currentLocationChildLocations]
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map((loc) => loc.id);
  }, [currentLocation, currentLocationChildLocations]);

  // Calculate globally "Fully Offline" locations to avoid duplication in nested views.
  const fullyOfflineLocationIds = useMemo(() => {
    const offlineIds = new Set<string>();

    sortedChildLocations.forEach((childLoc) => {
      const locSensors = allSensors.filter(
        (item) => item.location === childLoc || item.location?.startsWith(`${childLoc}#`)
      );
      const offlineSensors = locSensors.filter(
        (item) => item?.value !== undefined && item.value <= 0
      );

      if (locSensors.length > 0 && locSensors.length === offlineSensors.length) {
        offlineSensors.forEach((item) => {
          if (item.location) offlineIds.add(item.location);
        });
      }
    });
    return offlineIds;
  }, [allSensors, sortedChildLocations]);

  const handleTagging = (sensors: DataTreeItem[], isTagged: boolean) => {
    sensors.forEach(async (sensor) => {
      const sensorDetails = await fetchSensorLatest(sensor.id);
      // const currentTags = Array.from(new Set(sensorDetails.tags)) || [];
      const currentTags = sensorDetails.tags || [];
      const updatedTags = isTagged
        ? currentTags.filter((tag: string) => tag !== knownOfflineTag)
        : [...currentTags, knownOfflineTag];

      updateSensorProperties(sensor.id, { tags: updatedTags }).then((response) => {
        if (response.id) {
          const updatedSensorDetails = { ...sensorDetails, ...response };
          dispatch(setSensorsById([updatedSensorDetails]));
        }
      });
    });
  };

  if (!isDesktopView) return <div className={globalClasses.bodyContent} />;

  return (
    <Box
      className={globalClasses.bodyContent}
      sx={{ overflowY: 'scroll', height: '100%', padding: '10px' }}
    >
      <Header showSearchField={false} />
      <ToggleButtonGroup
        color="primary"
        size="small"
        value={listType}
        exclusive
        onChange={(_, value) => value && setListType(value)}
        sx={{ display: 'flex', justifyContent: 'end', marginTop: '8px' }}
      >
        <ToggleButton sx={{ textTransform: 'inherit' }} value={ListOptions.all}>
          Show All
        </ToggleButton>
        <ToggleButton sx={{ textTransform: 'none' }} value={ListOptions.untagged}>
          Show untagged only
        </ToggleButton>
      </ToggleButtonGroup>
      <List sx={{ background: 'none' }}>
        {fetchingSensorProps && <Loading />}
        {sortedChildLocations.map((childLocationId) => (
          <LocationRow
            key={childLocationId}
            locId={childLocationId}
            allSensors={allSensors}
            fullyOfflineLocs={fullyOfflineLocationIds}
            listType={listType}
            handleTagging={handleTagging}
          />
        ))}

        {allSensors.filter((item) => item?.value !== undefined && item.value <= 0).length === 0 && (
          <Alert severity="info">Sensors not found</Alert>
        )}
      </List>
    </Box>
  );
}

export default OfflineView;
